import { ApplicationConfig, provideZonelessChangeDetection } from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideNativeDateAdapter } from '@angular/material/core';
import { provideShareButtonsOptions } from 'ngx-sharebuttons';
import { shareIcons } from 'ngx-sharebuttons/icons';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import {
  centralHttpErrorInterceptor,
  LayoutService,
  provideCentralErrorHandler,
  provideErrorSnackbar,
  provideLocaleRouting,
  provideLoggingService,
  provideTranslocoService,
  RUNTIME_CONFIGURATION,
  TRANSLATION_SOURCE_REGISTRY,
} from '@processpuzzle/util';
import { AUTHENTICATION_CONFIGURATION, authTokenInterceptorFor, LOGOUT_REDIRECT_URL, provideAuthenticationService } from '@processpuzzle/auth/domain';
import { BASE_ENTITY_FACADE_REGISTRY, BASE_ENTITY_TRANSLATION_SOURCE, provideEntityRouteRegistry } from '@processpuzzle/base-entity';
import { ApplicationPropertyStore, BASE_WIDGET_TRANSLATION_SOURCE } from '@processpuzzle/base-widget';
import { ORG_ADMIN_ENTITY_FACADES, ORG_ADMIN_FACADE_PROVIDERS, ORG_ADMIN_ORG_KEY, ORG_ADMIN_TRANSLATION_SOURCE } from '@processpuzzle/org-admin';
import { PLATFORM_SUBSCRIPTION_TRANSLATION_SOURCE } from '@processpuzzle/platform-subscription';
import { RuntimeConfiguration } from './runtime-configuration';
import { createAppRoutes } from './app.routes';
import { emptyStarterCatalogOnNotFound } from './business-starters/starter-catalog';
import { provideBizApplicationContext } from './navigation/application-context';
import { BizApplicationPropertyMapper } from './services/biz-application-property.mapper';
import { BizApplicationPropertyService } from './services/biz-application-property.service';
import { BizApplicationPropertyStore } from './services/biz-application-property.store';

/**
 * @param runtimeConfiguration the deep-merged `run-time-conf` files, with the tenant realm already
 *   substituted by `main.ts`
 * @param orgKey the tenant the URL named, or `undefined` on the landing page
 */
export function createAppConfig(runtimeConfiguration: RuntimeConfiguration, orgKey?: string): ApplicationConfig {
  const orgAdminRoot = runtimeConfiguration.BASE_CONFIGURATION.ORG_ADMIN_SERVICE_ROOT;
  const subscriptionRoot = runtimeConfiguration.BASE_CONFIGURATION.SUBSCRIPTION_SERVICE_ROOT;
  const tokenRoots = [orgAdminRoot, subscriptionRoot].filter((root): root is string => !!root);

  return {
    providers: [
      provideZonelessChangeDetection(),
      // One `provideHttpClient` with both interceptors, rather than `provideAuthTokenInterceptor()`
      // next to a second `provideHttpClient` — two calls compete and only one chain survives.
      //
      // The auth interceptor is given the two backends this application's token means something
      // to, and nothing else. The subscription root matters only where it is absolute — the dev
      // server's `http://localhost:8081` — because a relative `/api` is same-origin and covered
      // anyway. Customer Home is why: its API refuses a request without the customer's token.
      //
      // `emptyStarterCatalogOnNotFound` comes last so it runs inside the central one: an unpublished
      // registry's 404 becomes an empty catalog before anything reports it as an error.
      provideHttpClient(withInterceptors([authTokenInterceptorFor(tokenRoots), centralHttpErrorInterceptor, emptyStarterCatalogOnNotFound])),
      provideAuthenticationService(runtimeConfiguration),
      provideLoggingService(runtimeConfiguration.LOGGING_CONFIGURATION),
      provideCentralErrorHandler(),
      provideErrorSnackbar(),
      // Application level, not route level, and for two separate reasons. `ORG_ADMIN_ORG_KEY` is
      // injected while `OrganizationUserService` — `providedIn: 'root'` — is constructed, which
      // happens before any route activates. And `ORG_ADMIN_ROUTES` binds `ACTIVE_ENTITY_FACADE`
      // with `useExisting`, which needs the facade instance to already exist; a route-level provider
      // would create a second one per activation and lose whatever the first had loaded.
      { provide: ORG_ADMIN_ORG_KEY, useValue: orgKey ?? '' },
      ...ORG_ADMIN_FACADE_PROVIDERS,
      {
        provide: BASE_ENTITY_FACADE_REGISTRY,
        useValue: { ...ORG_ADMIN_ENTITY_FACADES },
      },
      // Which backend serves which transloco scope, for bundles with no asset to fall back on. All
      // four contributions have to be here rather than on a route branch: a `multi` token is not
      // merged across injectors, so a route-level entry replaces the collection instead of extending
      // it. That is why `platform_subscription` is registered here and not inside
      // `SUBSCRIPTION_ROUTES`, which provides only the transloco *scope*.
      ...[ORG_ADMIN_TRANSLATION_SOURCE, BASE_ENTITY_TRANSLATION_SOURCE, BASE_WIDGET_TRANSLATION_SOURCE, PLATFORM_SUBSCRIPTION_TRANSLATION_SOURCE].map((source) => ({
        provide: TRANSLATION_SOURCE_REGISTRY,
        useValue: source,
        multi: true,
      })),
      { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
      { provide: RUNTIME_CONFIGURATION, useValue: runtimeConfiguration },
      { provide: AUTHENTICATION_CONFIGURATION, useValue: runtimeConfiguration.AUTHENTICATION_CONFIGURATION },
      // Admin URLs are guarded. Returning to the route that opened the logout dialog would start a
      // new Keycloak login immediately after the SSO session has been ended.
      { provide: LOGOUT_REDIRECT_URL, useValue: '/welcome' },
      provideRouter(createAppRoutes(orgKey), withComponentInputBinding()),
      provideEntityRouteRegistry(),
      provideNativeDateAdapter(),
      provideShareButtonsOptions(shareIcons()),
      provideTranslocoService(runtimeConfiguration.LANGUAGE_CONFIGURATION),
      // Must follow `provideRouter`: the later UrlSerializer provider adds the active language
      // to every browser URL while routes themselves stay language-agnostic.
      provideLocaleRouting(),
      LayoutService,
      // The name and navigation the header's `app-title` and `nav-menu` widgets read. base-app's
      // shell would provide it from an AppDefinition; this application has none, so it is built here.
      provideBizApplicationContext(),
      // The application properties the header's like button reads and writes, served by
      // `processpuzzle-biz-backend` over this site's own origin.
      //
      // This replaces `provideAppPropertyStore()`, which wired core's `ApplicationPropertyService`
      // — and that one resolves `THIRD_PARTY_ROOT`, the testbed application's json-server prefix.
      // In a deployed Biz that meant a cross-origin request to `testbed.<env>.processpuzzle.de`
      // on every page load, which failed CORS and popped an error snackbar at the visitor. See
      // `BizApplicationPropertyService`.
      //
      // The second entry binds the biz store to **core's token**. `LikeButtonComponent` injects
      // `ApplicationPropertyStore` and is core's, so overriding the token is what lets the widget
      // reach this backend with no change to `@processpuzzle/base-widget`. An explicit provider
      // here outranks that store's own `providedIn: 'root'`.
      //
      // `useFactory` rather than a class provider because `BaseEntityRestService` calls `inject()`
      // in field initialisers, so both have to be constructed inside an injection context.
      { provide: BizApplicationPropertyService, useFactory: () => new BizApplicationPropertyService(new BizApplicationPropertyMapper()) },
      { provide: ApplicationPropertyStore, useFactory: () => new BizApplicationPropertyStore(), deps: [BizApplicationPropertyService] },
    ],
  };
}
